/**
 * PDP product gallery (Swiper 11) — unique product-gallery* classes.
 * media-gallery keeps setActiveMedia for variant switching (main.js).
 * product-gallery-slider initializes the main + thumbs Swipers.
 */
if (!customElements.get('product-gallery-slider')) {
  customElements.define(
    'product-gallery-slider',
    class ProductGallerySlider extends HTMLElement {
      connectedCallback() {
        if (this.mainSwiper) return;

        const gallery = this.closest('media-gallery.product-gallery');
        const thumbsEl = gallery ? gallery.querySelector('.product-gallery-thumbs') : null;

        const init = () => {
          if (typeof Swiper === 'undefined' || this.mainSwiper) return;

          if (thumbsEl) {
            this.thumbsSwiper = new Swiper(thumbsEl, {
              spaceBetween: 5,
              slidesPerView: 5,
              freeMode: true,
              watchSlidesProgress: true,
              slideToClickedSlide: true,
              breakpoints: {
                750: {
                  slidesPerView: 5,
                  spaceBetween: 10,
                },
              },
            });
          }

          this.mainSwiper = new Swiper(this, {
            slidesPerView: 1,
            spaceBetween: 0,
            speed: 400,
            watchOverflow: true,
            simulateTouch: true,
            grabCursor: true,
            touchStartPreventDefault: false,
            mousewheel: {
              forceToAxis: true,
              releaseOnEdges: true,
              sensitivity: 1,
            },
            navigation: {
              nextEl: this.querySelector('.product-gallery-nav--next'),
              prevEl: this.querySelector('.product-gallery-nav--prev'),
            },
            thumbs: this.thumbsSwiper
              ? {
                  swiper: this.thumbsSwiper,
                }
              : undefined,
            on: {
              slideChange: () => this.onSlideChange(),
            },
          });

          if (gallery) {
            gallery.mainSwiper = this.mainSwiper;
            gallery.thumbsSwiper = this.thumbsSwiper;
          }
        };

        if (typeof Swiper !== 'undefined') {
          init();
        } else {
          window.addEventListener('load', init, { once: true });
        }
      }

      onSlideChange() {
        if (!this.mainSwiper) return;
        const activeSlide = this.mainSwiper.slides[this.mainSwiper.activeIndex];
        if (!activeSlide) return;

        this.querySelectorAll('.product-gallery-slide').forEach((el) => {
          el.classList.toggle('is-active', el === activeSlide);
        });

        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        const deferredMedia = activeSlide.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }
    }
  );
}

if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.prependMedia = this.dataset.disablePrepend != 'true';
        this.filteringOption = this.dataset.filteringOption;
      }

      connectedCallback() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
        if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();

        // Swiper PDP gallery is handled by product-gallery-slider
        if (this.classList.contains('product-gallery')) return;

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
        // Swiper PDP path
        if (this.classList.contains('product-gallery')) {
          const slider = this.querySelector('product-gallery-slider');
          const mainSwiper = this.mainSwiper || (slider && slider.mainSwiper);
          if (!mainSwiper) return;

          const slides = Array.from(mainSwiper.slides || []);
          const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
          if (index >= 0) {
            mainSwiper.slideTo(index);
            this.playActiveMedia(slides[index]);
          }
          return;
        }

        if (!this.elements || !this.elements.viewer) return;

        if (filtering && currentVariant) {
          const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
          allSlides.forEach((slide) => {
            if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
              slide.classList.remove('hidden');
            } else {
              slide.classList.add('hidden');
            }
          });
        }

        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend && this.prependMedia) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (filtering && currentVariant) {
          if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
          if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, false, 10))
          return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion || !window.accessibilityStrings) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer || !this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        if (this.elements.viewer.sliderItems) {
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }
    }
  );
}
